import { useQuery } from '@tanstack/react-query';
import { Monitor, RefreshCw, Smartphone } from 'lucide-react';
import { useState } from 'react';
import type { PagePreview } from '@barbeiropro/contracts';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { PageView, SiteShell } from '../public/site-view';

/** Renderiza o RASCUNHO salvo com os mesmos componentes do site público. */
export function PreviewPane({ pageId, draftVersion, dirty, onSave }: { pageId: string; draftVersion: number; dirty: boolean; onSave: () => void }) {
  const [mode, setMode] = useState<'desktop' | 'mobile'>('desktop');
  const q = useQuery({
    queryKey: ['site', 'preview', pageId, draftVersion],
    queryFn: () => api.get<PagePreview>(`/site/pages/${pageId}/preview`),
    placeholderData: (prev) => prev,
  });
  return (
    <div className="flex h-full min-h-[32rem] flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="group" aria-label="Tamanho da pré-visualização" className="inline-flex rounded-md border p-0.5">
          {(
            [
              ['desktop', 'Computador', Monitor],
              ['mobile', 'Celular', Smartphone],
            ] as const
          ).map(([m, label, Icon]) => (
            <Button key={m} type="button" size="sm" variant={mode === m ? 'secondary' : 'ghost'} aria-pressed={mode === m} onClick={() => setMode(m)}>
              <Icon aria-hidden /> {label}
            </Button>
          ))}
        </div>
        {dirty ? (
          <Button type="button" size="sm" variant="outline" onClick={onSave}>
            <RefreshCw aria-hidden /> Salvar para atualizar
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">Mostra o último rascunho salvo</span>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border bg-muted/60 p-3">
        {q.isPending && <LoadingState label="Montando pré-visualização…" />}
        {q.isError && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
        {q.data && (
          <div
            className={cn('mx-auto h-[calc(100vh-14rem)] min-h-[30rem] overflow-y-auto bg-white shadow-md', mode === 'mobile' ? 'w-[390px] max-w-full rounded-[1.75rem] border-8 border-[#1c2321]' : 'w-full rounded')}
            // contêiner de rolagem próprio: o cabeçalho "sticky" fica dentro da moldura
          >
            <SiteShell slug={q.data.site.organization.slug} site={q.data.site} pageSlug={q.data.slug} preview>
              <PageView page={q.data} />
            </SiteShell>
          </div>
        )}
      </div>
    </div>
  );
}
